/**
 * localStorage-backed persistence (Phase 5a).
 *
 * One key per scenario record: `branchworks:scenario:<id>`. The list is
 * derived by scanning keys with that prefix — no separate index to keep in
 * sync. At the scale this tool operates (a designer's personal library, tens
 * of scenarios, not thousands) scanning is fine; a real backend indexes.
 *
 * This is the durable-persistence answer for the no-backend phase. It solves
 * the "refresh-the-tab-lose-everything" problem the prototype had. When
 * Supabase lands (Phase 5b), this class is replaced by SupabaseRepository
 * implementing the identical interface — call sites don't change.
 *
 * Note: localStorage is synchronous, but the methods return Promises to match
 * the async ScenarioRepository contract. The `await` overhead is negligible
 * and the uniform interface is worth it.
 */
import type {
  ScenarioRepository,
  ScenarioSummary,
  StoredScenario,
} from './ScenarioRepository';

const KEY_PREFIX = 'branchworks:scenario:';

export class LocalStorageRepository implements ScenarioRepository {
  private keyFor(id: string): string {
    return KEY_PREFIX + id;
  }

  async list(): Promise<ScenarioSummary[]> {
    const summaries: ScenarioSummary[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const record = JSON.parse(raw) as StoredScenario;
        summaries.push({
          id: record.id,
          title: record.draft?.title || 'Untitled scenario',
          updatedAt: record.updatedAt,
          publishedCount: (record.publishedVersions || []).length,
        });
      } catch {
        // Corrupt entry — skip it rather than crashing the whole list.
        continue;
      }
    }
    // Most-recently-updated first.
    summaries.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return summaries;
  }

  async load(id: string): Promise<StoredScenario | null> {
    const raw = localStorage.getItem(this.keyFor(id));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredScenario;
    } catch {
      return null;
    }
  }

  async save(record: StoredScenario): Promise<void> {
    // Stamp updatedAt on every save so list ordering reflects reality.
    const stamped: StoredScenario = { ...record, updatedAt: new Date().toISOString() };
    try {
      localStorage.setItem(this.keyFor(record.id), JSON.stringify(stamped));
    } catch (e) {
      // Quota exceeded is the realistic failure here. Surface it; the store's
      // saveStatus will go to 'error' and the toolbar shows it.
      throw new Error(
        'Could not save to local storage (it may be full). Export your scenario as JSON to be safe.',
      );
    }
  }

  async remove(id: string): Promise<void> {
    localStorage.removeItem(this.keyFor(id));
  }
}
